import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, Clock, Users, ArrowRight, CalendarPlus } from "lucide-react";
import PublicNav from "@/components/PublicNav";
import Footer from "@/components/Footer";
import ParkMap from "@/components/ParkMap";
import { googleCalUrl, ICS_URL } from "@/lib/calendar";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function Schedule() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wlFor, setWlFor] = useState(null);
  const [wl, setWl] = useState({ name: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  useEffect(() => {
    api.get("/public/schedule").then((r) => setSessions(r.data)).finally(() => setLoading(false));
  }, []);

  const joinWaitlist = async (sessionId) => {
    if (!wl.name || !wl.email) { toast.error("Please add your name and email"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/public/waitlist", { session_id: sessionId, ...wl });
      toast.success(data.message || "You're on the waitlist!");
      setWlFor(null); setWl({ name: "", email: "", phone: "" });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="bg-white text-brand-ink min-h-screen">
      <PublicNav />
      <section className="pt-32 pb-16 px-6 lg:px-10 max-w-7xl mx-auto">
        <div className="text-xs uppercase tracking-[0.35em] text-brand-red font-semibold mb-5">Follow Along</div>
        <h1 className="font-display text-5xl sm:text-6xl">Park Schedule</h1>
        <p className="mt-6 font-serif text-2xl text-brand-ink/70 max-w-2xl">
          Track where and when we'll be practicing across Salt Lake City. Prebook your 45-minute session and bring your pup.
        </p>
      </section>

      {sessions.length > 0 && (
        <section className="px-6 lg:px-10 max-w-7xl mx-auto pb-10">
          <ParkMap sessions={sessions} />
        </section>
      )}

      <section className="pb-28 px-6 lg:px-10 max-w-7xl mx-auto">
        {loading ? (
          <div className="text-brand-ink/50" data-testid="schedule-loading">Loading sessions…</div>
        ) : sessions.length === 0 ? (
          <div className="rounded-2xl border border-brand-line p-12 text-center" data-testid="schedule-empty">
            <p className="font-serif text-2xl text-brand-ink/70">No sessions scheduled just yet.</p>
            <Link to="/book" className="mt-6 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-brand-red text-white font-medium hover:bg-brand-reddark transition-colors">Request a session <ArrowRight className="w-4 h-4" /></Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="schedule-grid">
            {sessions.map((s) => {
              const taken = s.spots_taken || 0;
                  const full = taken >= s.capacity;
              const left = Math.max(0, s.capacity - taken);
              return (
                <div key={s.id} className="rounded-2xl border border-brand-line p-7 hover:-translate-y-1 hover:shadow-xl transition-all duration-300 flex flex-col" data-testid={`schedule-card-${s.id}`}>
                  <div className="flex items-center gap-2 text-brand-red font-medium"><MapPin className="w-4 h-4" /> {s.park_name}</div>
                  <div className="text-sm text-brand-ink/50 mt-1">{s.city}{s.address ? ` · ${s.address}` : ""}</div>
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    {s.date === today && <span className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-brand-red px-2.5 py-1 rounded-full animate-pulse" data-testid={`here-now-${s.id}`}>● Here now · Today</span>}
                    {s.is_opener && <span className="text-xs font-semibold text-brand-red bg-brand-mist px-2.5 py-1 rounded-full">Season Opener</span>}
                    {s.weather_hold && <span className="text-xs font-semibold text-amber-700 bg-amber-100 px-2.5 py-1 rounded-full" data-testid={`weather-hold-${s.id}`}>Weather hold</span>}
                    {full && <span className="text-xs font-semibold text-white bg-brand-ink px-2.5 py-1 rounded-full" data-testid={`full-badge-${s.id}`}>Full</span>}
                    {!full && left <= 5 && <span className="text-xs font-semibold text-brand-red bg-red-50 px-2.5 py-1 rounded-full" data-testid={`spots-left-${s.id}`}>Only {left} left</span>}
                  </div>
                  <div className="mt-5 font-display text-3xl">{s.date}</div>
                  <div className="mt-2 flex items-center gap-4 text-sm text-brand-ink/60">
                    <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" /> {s.time}</span>
                    <span className={`flex items-center gap-1.5 ${!full && left <= 5 ? "text-brand-red font-semibold" : ""}`}><Users className="w-4 h-4" /> {full ? "Fully booked" : `${left} spot${left === 1 ? "" : "s"} left`}</span>
                  </div>
                  {s.notes && <p className="mt-4 text-sm text-brand-ink/60 leading-relaxed">{s.notes}</p>}
                  <div className="mt-auto pt-6 flex items-center justify-between">
                    <span className="font-display text-xl">${s.price_online}<span className="text-sm text-brand-ink/50"> online</span></span>
                    {full ? (
                      <button onClick={() => { setWlFor(wlFor === s.id ? null : s.id); setWl({ name: "", email: "", phone: "" }); }} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-brand-red text-brand-red text-sm font-medium hover:bg-brand-red hover:text-white transition-colors" data-testid={`join-waitlist-btn-${s.id}`}>Join waitlist</button>
                    ) : s.weather_hold ? (
                      <span className="text-sm text-amber-700">Weather hold</span>
                    ) : (
                      <Link to={`/book?session=${s.id}`} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-red text-white text-sm font-medium hover:bg-brand-reddark transition-colors" data-testid={`schedule-prebook-${s.id}`}>Prebook</Link>
                    )}
                  </div>

                  {full && wlFor === s.id && (
                    <div className="mt-4 pt-4 border-t border-brand-line space-y-2.5" data-testid={`waitlist-form-${s.id}`}>
                      <p className="text-xs text-brand-ink/50">Join the waitlist — we'll email you the moment a spot opens.</p>
                      <input value={wl.name} onChange={(e) => setWl({ ...wl, name: e.target.value })} placeholder="Your name" className="w-full rounded-lg border border-brand-line px-3 py-2 text-sm focus:ring-2 focus:ring-brand-red focus:outline-none" data-testid={`waitlist-name-${s.id}`} />
                      <input value={wl.email} onChange={(e) => setWl({ ...wl, email: e.target.value })} type="email" placeholder="Email" className="w-full rounded-lg border border-brand-line px-3 py-2 text-sm focus:ring-2 focus:ring-brand-red focus:outline-none" data-testid={`waitlist-email-${s.id}`} />
                      <input value={wl.phone} onChange={(e) => setWl({ ...wl, phone: e.target.value })} placeholder="Phone (optional)" className="w-full rounded-lg border border-brand-line px-3 py-2 text-sm focus:ring-2 focus:ring-brand-red focus:outline-none" data-testid={`waitlist-phone-${s.id}`} />
                      <button onClick={() => joinWaitlist(s.id)} disabled={submitting} className="w-full px-4 py-2.5 rounded-full bg-brand-red text-white text-sm font-medium hover:bg-brand-reddark transition-colors disabled:opacity-60" data-testid={`waitlist-submit-${s.id}`}>{submitting ? "Joining…" : "Join waitlist"}</button>
                    </div>
                  )}

                  <a href={googleCalUrl(s)} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 text-xs text-brand-ink/50 hover:text-brand-red transition-colors" data-testid={`gcal-${s.id}`}><CalendarPlus className="w-3.5 h-3.5" /> Add to Google Calendar</a>
                </div>
              );
            })}
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
